import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriminalSaveStep1, CriminalSaveStep2, CriminalSaveStep3, CriminalSaveStep4 } from '../../types/criminalApi';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { calcAgeFromDob } from '../../utils/caseFieldValidation';
import { formatDateRange, formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { IconWarningTriangle } from '../../components/icons/WizardIcons';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewListField,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { isBriberySubCategory } from '../wizard/criminal/criminalConstants';
import { useCriminalLookups } from '../wizard/criminal/useCriminalLookups';
import * as criminalService from '../../services/criminalCaseRequests';
import { apiOrLookupLabel, caseSourceDisplay, deliveryMethodDisplay, labelFor, submitterFromStep4 } from './viewRequestHelpers';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: CriminalWizardDraft;
  submittedAt: string;
  step1?: CriminalSaveStep1;
  step2?: CriminalSaveStep2;
  step3?: CriminalSaveStep3;
  step4?: CriminalSaveStep4;
};

function accusedLabel(
  name: string,
  statusId: string,
  statusOther: string,
  statuses: { id: string; name: string }[],
) {
  const statusName =
    statusOther.trim() ||
    (statusId === 'other' ? '' : statuses.find((s) => s.id === statusId)?.name ?? statusId);
  if (!name.trim()) return '—';
  return statusName ? `${name.trim()} (${statusName})` : name.trim();
}

export default function ViewRequestCaseDetailsTab({
  caseRequestId,
  draft,
  submittedAt,
  step1,
  step2,
  step3,
  step4,
}: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useCriminalLookups('review', draft.provinceId, draft.districtId);

  const applicableAreaLookupKey =
    draft.applicableAreas.length || !draft.subFileCategoryId || !draft.applicableAreaIds.length
      ? null
      : `${draft.subFileCategoryId}|${draft.applicableAreaIds.join(',')}`;

  const [fetchedAreaLabels, setFetchedAreaLabels] = useState<{ key: string; labels: string[] } | null>(null);

  useEffect(() => {
    if (!applicableAreaLookupKey) return;

    let mounted = true;
    const subFileCategoryId = draft.subFileCategoryId;
    const applicableAreaIds = draft.applicableAreaIds;

    void criminalService.getSubCategoryApplicableAreas(subFileCategoryId).then((r) => {
      if (!mounted || !r.success) return;
      const labels = applicableAreaIds
        .map((id) => r.data.find((a) => a.id === id)?.name ?? '')
        .filter(Boolean);
      setFetchedAreaLabels({ key: applicableAreaLookupKey, labels });
    });

    return () => {
      mounted = false;
    };
  }, [applicableAreaLookupKey]);

  const areaLabels = useMemo(() => {
    if (draft.applicableAreas.length) return draft.applicableAreas;
    if (!applicableAreaLookupKey) return [];
    if (fetchedAreaLabels?.key === applicableAreaLookupKey) return fetchedAreaLabels.labels;
    return [];
  }, [applicableAreaLookupKey, draft.applicableAreas, fetchedAreaLabels]);

  const subName = draft.subFileCategoryName || step1?.sub_category_name || '—';
  const showHighCourt = useMemo(
    () => isBriberySubCategory(draft.subFileCategoryName || step1?.sub_category_name || ''),
    [draft.subFileCategoryName, step1?.sub_category_name],
  );
  const victimAge = useMemo(() => calcAgeFromDob(draft.victimDob), [draft.victimDob]);
  const under18 = typeof victimAge === 'number' && victimAge < 18;

  const caseSourceLabel = caseSourceDisplay(draft, lookups.caseSources, step1);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft, lookups.deliveryMethods, step4, noData),
    [draft, lookups.deliveryMethods, step4, noData],
  );

  const connectedLabel =
    draft.hasConnectedMatters === 'yes' ? t('common.yes') : draft.hasConnectedMatters === 'no' ? t('common.no') : noData;

  const showConnected = draft.hasConnectedMatters === 'yes';

  const submitter = useMemo(() => submitterFromStep4(step4), [step4]);

  const genderLabel =
    step2?.gender_name?.trim() ||
    labelFor(draft.victimGenderId, lookups.genders) ||
    draft.victimGender;

  const urgencyLabel =
    step3?.urgency_level_name?.trim() || labelFor(draft.urgencyLevelId, lookups.urgencyLevels);

  const submittedAtLabel = formatDisplayDateTime(submittedAt) || noData;

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        {t('viewRequest.requestSubmittedAt')} <strong>{submittedAtLabel}</strong>
      </p>

      <ReviewSection step={t('wizard.reviewStepLabel', { num: '01' })} title={t('wizard.criminal.steps.1')}>
        <ReviewSubBlock title={t('wizard.sections.caseIntakeDetails')}>
          <ReviewGrid cols={4}>
            <ReviewField label={t('wizard.fields.fileCategory')} value={draft.fileCategory || step1?.category_name || ''} />
            <ReviewField label={t('wizard.fields.subFileCategory')} value={subName} />
          </ReviewGrid>
          <ReviewChips items={areaLabels} />
          <ReviewField label={t('wizard.fields.description')} value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label={t('wizard.fields.caseSource')} value={caseSourceLabel} />
            <ReviewField
              label={t('wizard.fields.province')}
              value={apiOrLookupLabel(step1?.province_name, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label={t('wizard.fields.district')}
              value={apiOrLookupLabel(step1?.district_name, draft.districtId, lookups.districts)}
            />
            {showHighCourt ? (
              <ReviewField
                label={t('wizard.fields.highCourtName')}
                value={apiOrLookupLabel(step1?.high_court_name, draft.highCourtId, lookups.highCourts)}
              />
            ) : null}
            <ReviewField
              label={t('wizard.fields.magistrateCourtName')}
              value={apiOrLookupLabel(step1?.magistrate_court_name, draft.magistrateCourtId, lookups.magistrateCourts)}
            />
            <ReviewField
              label={t('wizard.fields.policeStation')}
              value={apiOrLookupLabel(step1?.police_station_name, draft.policeStationId, lookups.policeStations)}
            />
            <ReviewField label={t('wizard.fields.yourReferenceNumber')} value={draft.yourReferenceNumber} />
            <ReviewField label={t('wizard.fields.otherExternalReferences')} value={draft.otherExternalReferences} />
            <ReviewField label={t('wizard.fields.caseNumber')} value={draft.caseNumber} />
            <ReviewField label={t('wizard.fields.connectedMatters')} value={connectedLabel} />
            {showConnected ? (
              <>
                <ReviewField label={t('wizard.fields.connectedCaseNumber')} value={draft.connectedCaseNumber} />
                <ConnectedAgdFileRefReviewField
                  caseRequestId={caseRequestId}
                  label={t('wizard.fields.connectedAgdFileRef')}
                  fileRef={draft.connectedAgdFileRef}
                />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={t('wizard.reviewStepLabel', { num: '02' })} title={t('wizard.criminal.steps.2')}>
        <ReviewSubBlock title={t('wizard.sections.victimDetails')}>
          <ReviewGrid cols={4}>
            <ReviewField label={t('wizard.fields.victimName')} value={draft.victimName} />
            <ReviewField label={t('wizard.fields.dateOfBirth')} value={formatDisplayDate(draft.victimDob)} />
            <div className="ccr-review-field">
              <div className="ccr-review-lbl">{t('wizard.fields.age')}</div>
              <div className="ccr-review-age-row">
                <span className="ccr-review-val">{typeof victimAge === 'number' ? String(victimAge) : '—'}</span>
                {under18 ? (
                  <span className="ccr-review-badge vcr-under18">
                    <IconWarningTriangle />
                    {t('wizard.childUnder16')}
                  </span>
                ) : null}
              </div>
            </div>
            <ReviewField label={t('wizard.fields.gender')} value={genderLabel} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={t('wizard.reviewStepLabel', { num: '03' })} title={t('wizard.criminal.steps.3')}>
        <ReviewSubBlock title={t('wizard.sections.offenceDetails')}>
          <ReviewGrid cols={3}>
            <ReviewField label={t('wizard.fields.dateOfOffence')} value={formatDateRange(draft.offenceDateFrom, draft.offenceDateTo)} />
            <ReviewField label={t('wizard.fields.placeOfOffence')} value={draft.placeOfOffence} />
            <ReviewField
              label={t('wizard.fields.accusedDetailsAvailable')}
              value={draft.accusedDetailsAvailable ? t('common.yes') : t('common.no')}
            />
            {draft.accusedDetailsAvailable ? (
              <ReviewListField
                label={t('wizard.fields.accusedSuspects')}
                items={draft.accused.map((a) =>
                  accusedLabel(a.name, a.status, a.statusOther, lookups.suspectStatuses),
                )}
              />
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title={t('wizard.sections.timeline')}>
          <ReviewGrid cols={3}>
            <ReviewField label={t('wizard.fields.urgencyLevel')} value={urgencyLabel} />
            <ReviewField label={t('wizard.fields.deadline')} value={formatDisplayDate(draft.deadline)} />
            <ReviewField label={t('wizard.fields.urgencyJustification')} value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={t('wizard.reviewStepLabel', { num: '04' })} title={t('wizard.criminal.steps.4')}>
        <ReviewSubBlock title={t('wizard.sections.requestSubmitterDetails')}>
          <ReviewGrid cols={4}>
            <ReviewField label={t('wizard.fields.submittedAgencyName')} value={submitter.agency} />
            <ReviewField label={t('wizard.fields.designation')} value={submitter.designation} />
            <ReviewField label={t('wizard.fields.submittedBy')} value={submitter.name} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label={t('wizard.fields.emailAddress')} value={submitter.email} />
            <ReviewField label={t('wizard.fields.contactNumber')} value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title={t('wizard.sections.physicalFileDeliveryMethod')}>
          <ReviewField label={t('wizard.fields.physicalFileDeliveryMethod')} value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>
    </div>
  );
}
